import Editor from "@draft-js-plugins/editor";
import createMentionPlugin, { MentionData, defaultSuggestionsFilter } from "@draft-js-plugins/mention";
import "@draft-js-plugins/mention/lib/plugin.css";
import { Loading, useDataQuery } from "@lyfegen-platform/react-common";
import { DraftHandleValue, EditorState, KeyBindingUtil } from "draft-js";
import { ReactElement, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import { SupportedMessageNodes } from "../../../models/generated/graphql";
import { initialNodeInputMessageState } from "../../../store/message";
import "./draftJSEditor.css";
import { GET_USERS_TO_MENTION_ON_CASE_MESSAGE } from "./queries";
import { GetUsersToMentionOnCaseMessageQuery } from "./types/queries.generated";
import useMessagesStyles from "./use-messages-styles";
const { hasCommandModifier } = KeyBindingUtil;

export interface MentionedUser {
	email: string;
	firstName: string;
	lastName: string;
}

interface MessageInputProps {
	currentUserEmail: string;
	nodeId: string;
	nodeLabel: SupportedMessageNodes;
	editorstates: {
		editorState: EditorState;
		setEditorState: React.Dispatch<React.SetStateAction<EditorState>>;
	};
	handleSend: () => void;
}

const maxContentLength = 1999;

export default function CustomMessageInput(props: MessageInputProps): ReactElement {
	const {
		nodeId,
		nodeLabel,
		editorstates: { editorState, setEditorState },
		handleSend,
	} = props;
	const ref = useRef<Editor>(null);
	const [open, setOpen] = useState(false);
	const setInput = useSetRecoilState(initialNodeInputMessageState(nodeId));
	const { data, loading } = useDataQuery<GetUsersToMentionOnCaseMessageQuery>(GET_USERS_TO_MENTION_ON_CASE_MESSAGE, {
		variables: { id: nodeId, nodeLabel: nodeLabel },
		fetchPolicy: "network-only",
	});
	const mentions: Array<MentionData> = useMemo(
		() =>
			(data?.GetUsersToMentionOnCaseMessage || []).map(
				(u) =>
					({
						link: u.email,
						name: `${u.firstName} ${u.lastName}`,
					} as MentionData)
			),
		[data?.GetUsersToMentionOnCaseMessage]
	);
	const [suggestions, setSuggestions] = useState(mentions);
	const { inputField } = useMessagesStyles();
	const { t } = useTranslation();

	const { MentionSuggestions, plugins } = useMemo(() => {
		const mentionPlugin = createMentionPlugin({
			mentionPrefix: "@",
		});
		// eslint-disable-next-line no-shadow
		const { MentionSuggestions } = mentionPlugin;
		// eslint-disable-next-line no-shadow
		const plugins = [mentionPlugin];
		return { plugins, MentionSuggestions };
	}, []);

	const onOpenChange = useCallback((_open: boolean) => {
		setOpen(_open);
	}, []);
	const onSearchChange = useCallback(
		({ value }: { value: string }) => {
			setSuggestions(defaultSuggestionsFilter(value, mentions));
		},
		[mentions]
	);

	const onInputChange = (e: React.SetStateAction<EditorState>) => {
		setEditorState(e);
		const input = editorState.getCurrentContent().getPlainText();
		setInput(input);
	};

	const handleMaxLimit = (): DraftHandleValue => {
		const currentContentLength = editorState.getCurrentContent().getPlainText().length;

		if (currentContentLength > maxContentLength) {
			console.info("you can type max 2000 characters");

			return "handled";
		}

		return "not-handled";
	};

	const sendMessageKeyBinding = (e: React.KeyboardEvent<{}>): string | null | undefined => {
		if (e.key === "Enter" && hasCommandModifier(e)) {
			return "send-message";
		}
		return undefined;
	};

	const handleKeyCommand = (command: string): DraftHandleValue => {
		setInput(editorState.getCurrentContent().getPlainText());
		if (command === "send-message") {
			handleSend();
			return "handled";
		}
		return "not-handled";
	};

	if (loading) return <Loading size="18px" />;

	return (
		<div
			className={inputField}
			onClick={() => {
				ref.current!.focus();
			}}
		>
			<Editor
				handleBeforeInput={handleMaxLimit}
				editorKey={"editor"}
				editorState={editorState}
				onChange={onInputChange}
				plugins={plugins}
				ref={ref}
				placeholder={t("type-your-message-here")}
				keyBindingFn={sendMessageKeyBinding}
				handleKeyCommand={handleKeyCommand}
			/>

			<MentionSuggestions
				open={open}
				onOpenChange={onOpenChange}
				suggestions={suggestions}
				onSearchChange={onSearchChange}
				onAddMention={() => {
					// get the mention object selected
				}}
			/>
		</div>
	);
}
